import type { AsyncDataOptions } from '#app';

/**
 * `useLazyAsyncData` for data a route cannot render without.
 *
 * `useAsyncData` catches whatever the handler throws and parks it in `error`
 * instead of letting it reach Nitro, so SSR answers 200 with an empty page and
 * a missing entity is served to crawlers as a soft 404. Rethrowing restores the
 * status code that already sits on the error — frontkit's `usePage` and
 * `useCertificate` raise `createError({ statusCode: 404, fatal: true })`, and
 * our own handlers do the same when the CMS answers with nothing.
 *
 * Use it only for the fetch that decides whether the page exists. Secondary
 * data (paginated lists, dependent dropdowns) should stay on
 * `useLazyAsyncData`, where a failure leaves the page up.
 */
export default async function useRequiredAsyncData<T>(
  key: string,
  handler: () => Promise<T>,
  options?: AsyncDataOptions<T>,
) {
  const result = await useLazyAsyncData<T>(key, handler, options);

  if (result.error.value) throw result.error.value;

  return result;
}
